// Utils
import { postMessage } from "../services/NativePostMessage";

// Services
import parseNestedUrl from "./parseNestedUrl";
import { decodeHTMLEntities } from "../services/AccessibleTextService";

export const LOCATION_PERMISSIONS = {
  ALWAYS: "always",
  WHEN_IN_USE: "whenInUse",
  DENIED: "denied",
  NOT_DETERMINED: "notDetermined",
  RESTRICTED: "restricted",
  UNKNOWN: "unknown",
  GRANTED: "granted",
  PROMPT: "prompt",
  UNAVAILABLE: "unavailable",
} as const;

export const getHostPrefix = () => window.location.host.split(".")[0];

export type LocationPermissionStatus =
  (typeof LOCATION_PERMISSIONS)[keyof typeof LOCATION_PERMISSIONS];

declare global {
  interface Window {
    appVersionCallback?: (version: string, build: string) => void;
    locationCallback?: (latitude: number, longitude: number, error?: string) => void;
    locationPermissionCallback?: (permissionStatus: LocationPermissionStatus) => void;
  }
}

export interface IButtonAction {
  type:
  | "IN_APP_BROWSER"
  | "IN_APP"
  | "OPEN_WALLET"
  | "CONTACT"
  | "URL"
  | "SHARE"
  | "CLOSE_ONBOARDING_MODAL";
  destinationPageId?: string;
  url?: string;
  supportEmail?: string;
  supportEmailSubject?: string;
  supportPhoneNumber?: string;
  device_dependent_url?: boolean;
  android_url?: string;
  ios_url?: string;
  hideToolBar?: boolean;
  traverseWebHistoryOnBackPressed?: boolean;
  service?: object;
  title?: string;
  text?: string;
}

export const handleOverrideClassName = (pageClassname) => pageClassname || "";

export const handleOverrideConfig = (themeConfig, pageConfig) => pageConfig || themeConfig;

export const limitItems = (items, limit) => (items?.length > limit ? items.slice(0, limit) : items);

export const handleJsBridgeForAppVersion = async () =>
  new Promise((resolve, reject) => {
    if (window.webkit) {
      // Native will call this function to pass the version and build number
      // let script = "appVersionCallback(\(version), \(build))"
      // webView.evaluateJavaScript(script)
      window.appVersionCallback = (version, build) => {
        resolve({ version, build });
      };

      postMessage("getAppVersion");
    } else if (window.Android) {
      try {
        const result = JSON.parse(postMessage("getAppVersion"));
        resolve(result);
      } catch {
        reject("Error in parsing the app version");
      }
    } else {
      resolve(null);
    }
  });

export const getCurrentLocation = (): Promise<GeolocationCoordinates | null> =>
  new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log("position", position);
          resolve(position.coords);
        },
        (error) => {
          reject(error);
        }
      );
    } else {
      reject(new Error("Geolocation is not supported by this browser"));
    }
    // }
  });

// Add this to CommonFunctions.ts
export const checkLocationPermissionState = async (): Promise<
  "granted" | "denied" | "prompt" | "unavailable"
> => {
  // Check if geolocation API is supported
  if (!navigator.geolocation) {
    console.log("Location permission check: Geolocation API not supported");
    return "unavailable";
  }

  // Check if we're in a secure context (required for geolocation)
  if (
    !window.isSecureContext &&
    window.location.protocol !== "https:" &&
    window.location.hostname !== "localhost"
  ) {
    console.log("Location permission check: Not in secure context");
    return "unavailable";
  }

  // Check permission state without prompting
  if (navigator.permissions) {
    try {
      const permission = await navigator.permissions.query({
        name: "geolocation" as PermissionName,
      });

      // Debug logging to see what we get
      console.log("Location permission state:", {
        state: permission.state,
        permission: permission,
        onchange: typeof permission.onchange,
      });

      return permission.state as "granted" | "denied" | "prompt";
    } catch (error) {
      console.log("Location permission check: Permissions API failed:", error);
      // If permissions API fails, assume we need to prompt
      return "prompt";
    }
  }
  console.log("Location permission check: Permissions API not available");
  // If permissions API is not available, assume we need to prompt
  return "prompt";
};

export const getOnboardingPageName = (locationPath) => {
  const flowName = window.config?.pages?.homepage?.flow?.[0];
  const flowPages = window.config?.flows?.[flowName]?.flow_pages;
  const pageNumber = parseInt(locationPath.split("/").pop(), 10);
  const arrayPosition = pageNumber;
  const pageName = flowPages?.[arrayPosition];
  return pageName;
};

export const sendAnalyticsClickEvent = (buttonTitle: string, pageName: string) => {
  sendAnalyticsEvent("onClick_event", pageName, buttonTitle);
};

// Lazy-loaded PWA analytics service
let trackPWAEventPromise: Promise<typeof import("../services/WebAnalyticsService").default> | null = null;

const getTrackPWAEvent = () => {
  if (!trackPWAEventPromise) {
    trackPWAEventPromise = import("../services/WebAnalyticsService").then(
      (module) => module.default
    );
  }
  return trackPWAEventPromise;
};

export const sendAnalyticsEvent = (name: string, page: string, action: string) => {
  // iOS Native (check for actual bridge, not just webkit)
  if (window.webkit?.messageHandlers?.ToApp) {
    window.webkit.messageHandlers.ToApp.postMessage({
      type: "ANALYTICS",
      actions: name,
      page: page,
      reason: action,
    });
  } else if (window.Android) {
    // Android Native
    window.Android.registerAnalyticsEvent(name, page, action);
  } else {
    // PWA/Web: Track via web analytics if running as installed PWA
    getTrackPWAEvent()
      .then((trackPWAEvent) => trackPWAEvent(name, page, action))
      .catch((error) => {
        console.error("Failed to track PWA analytics event:", error);
      });
  }
};

export const sanitizeText = (str) => {
  if (!str || typeof str !== "string") return "";

  return decodeHTMLEntities(str)
    .replace(/<[^>]+>/g, "") //
    .normalize("NFKD") // Normalize accented letters
    .replace(/[^\u0020-\u007E]/g, "") // Remove non-ASCII characters
    .replace(/\s+/g, " ") // Collapse whitespace/newlines into space
    .replace(/https?:\/\/\S+/g, "[link]") // Replace URLs with placeholder (optional)
    .trim();
};

export const handleRedirect = async (
  button_action: IButtonAction,
  history: any,
  buttonTitle: string,
  pageName: string,
  state?: Record<string, any>
) => {
  const tempPageName = pageName || "homepage";
  const sanitizedTitle = sanitizeText(buttonTitle).slice(0, 30);

  if (button_action) {
    const url = handleDeviceDependentUrl(button_action);

    const webLinkParams = {
      browserType: button_action.type,
      url: url,
      title: sanitizedTitle,
      hideToolBar: button_action.hideToolBar ? button_action.hideToolBar : false,
      traverseWebHistoryOnBackPressed: button_action.traverseWebHistoryOnBackPressed
        ? button_action.traverseWebHistoryOnBackPressed
        : false,
    };

    if (
      button_action.type === "URL" ||
      button_action.type === "IN_APP_BROWSER"
    ) {
      postMessage("openWebLink", webLinkParams);
    }

    // handling the separate behaviors of IN_APP
    if (button_action.type === "IN_APP") {
      if (button_action.url) {
        const targetUrl = new URL(button_action.url, window.location.origin);
        const isSameOrigin = targetUrl.origin === window.location.origin;
        
        if (isSameOrigin && !getPlatform().native) {
          // PWA with same-origin: use React Router
          history(targetUrl.pathname + targetUrl.search + targetUrl.hash);
        } else {
          // Native or external URL: use native bridge
          postMessage("openWebLink", webLinkParams);
        }
      } else if (button_action.destinationPageId) {
        // Relative path: always use React Router
        const fullPath = button_action.destinationPageId.startsWith("/")
          ? button_action.destinationPageId
          : `/${button_action.destinationPageId}`;
        history(fullPath);
      }
    }

    if (button_action.type === "OPEN_WALLET") {
      postOnboardingActions();

      if (window.webkit) {
        window.webkit.messageHandlers.ToApp.postMessage("WebPageCallbackOpenWallet");
      }

      if (window.Android) {
        window.Android.openWallet();
      }
    }

    if (button_action.type === "CLOSE_ONBOARDING_MODAL") {
      postOnboardingActions();
    }

    if (button_action.type === "SHARE") {
      if (navigator.share) {
        navigator
          .share({
            text: button_action.text,
            title: button_action.title,
            url: button_action.url,
          })
          .then(() => {
            console.log("Successfully shared");
          })
          .catch((err) => {
            console.error("Error sharing:", err);
          });
      } else if (window.Android) {
        window.Android.share(button_action.text, button_action.title, button_action.url);
      } else {
        console.error("Sharing is not supported on this browser.");
      }
    }

    if (button_action.type === "CONTACT") {
      const contactInfo = {
        supportEmail: button_action.supportEmail,
        supportEmailSubject: button_action.supportEmailSubject,
        supportPhoneNumber: button_action.supportPhoneNumber,
      };

      postMessage("showContactSheet", contactInfo);
    }
  }

  // Only include pass FSA in analytics if it exists (was collected from LocationPage)
  if (state?.fsa) {
    sendAnalyticsEvent("postal_code_event", tempPageName, state.fsa);
  }

  sendAnalyticsClickEvent(sanitizedTitle, tempPageName);
};

export const handleSliceContent = (numberOfItemsToShow, items) => {
  let itemReturn = items;
  if (numberOfItemsToShow && items?.length > numberOfItemsToShow) {
    itemReturn = items.splice(0, numberOfItemsToShow);
  }

  return itemReturn;
};

/**
 * This is a helper function to handle the device dependent url
 * @param button_action IButtonAction
 * @returns url - string
 */
export const handleDeviceDependentUrl = (button_action: IButtonAction) => {
  const url = button_action.device_dependent_url
    ? window.Android
      ? button_action.android_url
      : button_action.ios_url
    : button_action.url;

  return url;
};

export const setWebOnboardingPresented = (flag: boolean) => {
  const value = {
    webOnboardingPresented: flag,
  };

  postMessage("setValues", value, true);
};

export const setBackButtonVisibility = (visibility: boolean) => {
  postMessage("setBackButtonVisible", visibility);
};

export const setHideBackForUrls = (urls: string[]) => {
  const values = {
    urls: urls,
  };
  postMessage("hideBackForUrls", values);
};

export const postOnboardingActions = () => {
  // what is the below function doing ? i see same behaviour when true or false
  setWebOnboardingPresented(true);

  const { basePathname, currentPageNumber } = parseNestedUrl(window.location.href);

  if (!Number.isNaN(currentPageNumber)) {
    window.location.assign(basePathname);
  }
};

export const getLocation = (): Promise<GeolocationCoordinates | null> =>
  new Promise((resolve, reject) => {
    if (window.webkit || window.Android) {
      window.locationCallback = (latitude: number, longitude: number, error?: string) => {
        if (error) {
          reject(new Error(error));
        } else {
          resolve({ latitude, longitude } as GeolocationCoordinates);
        }
      };
      postMessage("getLocation");
    } else if (navigator.geolocation) {
      // Web: Use browser geolocation API
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve(position.coords);
        },
        (error) => {
          reject(error);
        }
      );
    } else {
      reject(new Error("Geolocation is not supported by this browser"));
    }
  });

export const getLocationPermission = (): Promise<LocationPermissionStatus> =>
  new Promise((resolve) => {
    if (window.webkit) {
      // iOS: Use native bridge with callback
      window.locationPermissionCallback = (permissionStatus: LocationPermissionStatus) => {
        console.log("permissionStatus", permissionStatus);
        resolve(permissionStatus);
      };
      postMessage("getLocationPermission");
    } else if (window.Android) {
      // Android: Use native bridge
      try {
        const result = postMessage("getLocationPermission");
        resolve(result as LocationPermissionStatus);
      } catch (error) {
        resolve("unknown");
      }
    } else {
      // Web: Use browser permissions API
      checkLocationPermissionState().then((status) => {
        resolve(status as LocationPermissionStatus);
      });
    }
  });
export const generateOnboardingPathAndKey = (
  Component,
  j,
  useIncrementalPath = false,
  basePath = ""
) => {
  const identifier = useIncrementalPath ? j.toString() : Component.displayName;
  const path = `${basePath}${window.GLOBAL_PATH}${identifier}`;
  const key = `${identifier}-${j}`;

  return { path, key };
};

/**
 * Check where the app was rendered. Used to check if we're using Android/iOS native webview
 * wrappers instead of Web/PWA
 * @returns if the app is within a native wrapper, and the os
 */
export const getPlatform = (): { native: boolean; os: "iOS" | "Android" | "unknown" } => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  if (window?.webkit && window?.webkit?.messageHandlers && !/CriOS|FxiOS|EdgiOS/.test(userAgent))
    return { native: true, os: "iOS" };
  if (window?.Android) return { native: true, os: "Android" };
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) return { native: false, os: "iOS" };
  if (/android/i.test(userAgent)) return { native: false, os: "Android" };
  return { native: false, os: "unknown" };
};
